import React, { useState, useEffect, useCallback } from 'react'
import { DollarSign, Clock, AlertTriangle, CheckCircle2, Download, User, Users } from 'lucide-react'
import {
  Card, CardContent, Button, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import { getDashboardStats, getCobranzaPendiente } from '../../../lib/supabase'
import { exportDashboardPdf, exportCobranzaExcel } from '../../../utils/pagosExport'
import { formatMoney } from '../../../utils/helpers'

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center gap-2.5">
        <div className={`p-1.5 rounded-lg shrink-0 ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground truncate">{label}</p>
          <p className="text-base sm:text-xl font-bold tabular-nums truncate">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

const DashboardTab = ({ temporadaId, temporadaSelector = null }) => {
  const [stats, setStats] = useState(null)
  const [cobranza, setCobranza] = useState({ jugadores: [], familias: [] })
  const [loading, setLoading] = useState(true)
  const [cobranzaView, setCobranzaView] = useState('jugador')

  const load = useCallback(async () => {
    setLoading(true)
    const [statsRes, cobranzaRes] = await Promise.all([
      getDashboardStats(temporadaId),
      getCobranzaPendiente(temporadaId),
    ])
    setStats(statsRes.data)
    setCobranza(cobranzaRes.data || { jugadores: [], familias: [] })
    setLoading(false)
  }, [temporadaId])

  useEffect(() => { load() }, [load])

  if (loading || !stats) {
    return <p className="text-muted-foreground text-sm py-8 text-center">Cargando estadisticas...</p>
  }

  const porcentajeCobrado = stats.total_cargos > 0
    ? ((stats.total_cobrado / stats.total_cargos) * 100).toFixed(1)
    : 0

  const handleExportPdf = () => {
    exportDashboardPdf(stats, { orgName: 'Club', temporada: '' })
  }

  const cobranzaList = cobranzaView === 'jugador' ? cobranza.jugadores : cobranza.familias

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">{temporadaSelector}</div>
        <Button variant="outline" size="sm" onClick={handleExportPdf} className="shrink-0" title="Descargar PDF">
          <Download className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Descargar PDF</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={DollarSign} label="Total por cobrar" value={formatMoney(stats.total_cargos)} color="bg-blue-100 text-blue-700" />
        <StatCard icon={CheckCircle2} label="Cobrado" value={formatMoney(stats.total_cobrado)} color="bg-green-100 text-green-700" />
        <StatCard icon={Clock} label="Pendiente" value={formatMoney(stats.total_pendiente)} color="bg-amber-100 text-amber-700" />
        <StatCard icon={AlertTriangle} label="Vencido" value={formatMoney(stats.total_vencido)} color="bg-red-100 text-red-700" />
      </div>

      {/* De donde sale el "por cobrar": precio de lista menos lo que el club
          decidio no cobrar. Solo aparece cuando hay descuentos, para no meter
          una linea de ceros en los clubes que no los usan. */}
      {stats.total_descuentos > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-1 text-sm">
              <span className="text-muted-foreground">
                Precio de lista{' '}
                <strong className="text-foreground tabular-nums ml-1">{formatMoney(stats.total_lista)}</strong>
              </span>
              <span className="text-muted-foreground">
                Descuentos otorgados{' '}
                <strong className="text-foreground tabular-nums ml-1">−{formatMoney(stats.total_descuentos)}</strong>
              </span>
              <span className="text-muted-foreground">
                Esperado neto{' '}
                <strong className="text-foreground tabular-nums ml-1">{formatMoney(stats.total_cargos)}</strong>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress bar */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Avance de cobranza</span>
            <span className="text-sm text-muted-foreground">{porcentajeCobrado}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full transition-all"
              style={{ width: `${Math.min(porcentajeCobrado, 100)}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
            <span>{stats.num_pagados} pagados</span>
            <span>{stats.num_parciales} parciales</span>
            <span>{stats.num_pendientes} pendientes</span>
            <span>{stats.num_vencidos} vencidos</span>
          </div>
        </CardContent>
      </Card>

      {/* Cobranza pendiente */}
      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold">Cobranza Pendiente</h3>
          {/* min-w-0 + flex-wrap: sin esto el bloque no podia encogerse y se salia de la pantalla */}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <div className="flex bg-muted rounded-lg p-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setCobranzaView('jugador')}
                className={`h-8 px-3 rounded text-sm font-medium transition-all ${cobranzaView === 'jugador' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                <User className="h-3.5 w-3.5 mr-1" /> Jugadores
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setCobranzaView('familia')}
                className={`h-8 px-3 rounded text-sm font-medium transition-all ${cobranzaView === 'familia' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                <Users className="h-3.5 w-3.5 mr-1" /> Familias
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              className="h-8 shrink-0"
              disabled={cobranzaList.length === 0}
              onClick={() => exportCobranzaExcel(cobranzaList, { view: cobranzaView })}
              title="Exportar Excel"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Exportar Excel</span>
            </Button>
          </div>
        </div>

        {cobranzaList.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-50" />
              <p>No hay cobranza pendiente en esta temporada</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{cobranzaView === 'jugador' ? 'Jugador' : 'Familia'}</TableHead>
                    {cobranzaView === 'familia' && <TableHead>Jugadores</TableHead>}
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">Vencido</TableHead>
                    <TableHead className="text-center">Estatus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cobranzaList.map((item, idx) => {
                    const nombre = cobranzaView === 'jugador'
                      ? item.jugador?.nombre_completo
                      : item.nombre
                    const saldo = item.saldo
                    const vencido = item.vencido

                    let estatusBadge
                    if (vencido > 0) {
                      estatusBadge = <Badge className="bg-red-100 text-red-800 text-xs">Vencido</Badge>
                    } else if (saldo > 0) {
                      estatusBadge = <Badge className="bg-amber-100 text-amber-800 text-xs">Pendiente</Badge>
                    }

                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{nombre}</span>
                            {cobranzaView === 'jugador' && item.jugador?.numero && (
                              <Badge variant="outline" className="text-[10px]">#{item.jugador.numero}</Badge>
                            )}
                            {cobranzaView === 'jugador' && item.familia && (
                              <span className="text-xs text-muted-foreground">{item.familia.nombre}</span>
                            )}
                          </div>
                        </TableCell>
                        {cobranzaView === 'familia' && (
                          <TableCell className="text-sm text-muted-foreground">
                            {(item.jugadores || []).map((j) => j.jugador?.nombre_completo).join(', ')}
                          </TableCell>
                        )}
                        <TableCell className="text-right font-medium text-sm">{formatMoney(saldo)}</TableCell>
                        <TableCell className="text-right text-sm">
                          {vencido > 0 ? (
                            <span className="text-red-600 font-medium">{formatMoney(vencido)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">{estatusBadge}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default DashboardTab
